import { MockServiceStore } from "./MockServiceStore";

/**
 * StructureCast - Vendor (purchasing supplier)
 *
 * Captures the suppliers used to source raw materials and consumables for
 * Mid-State Concrete Products' manhole production. Categories overlap with RawMaterial
 * categories ("concrete", "steel", "hardware", "chemical", "formwork") so
 * purchase orders can be quickly filtered by what each vendor supplies.
 */
export interface StructureCastVendor {
  id: string;
  vendorCode: string;
  vendorName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  categories: Array<"concrete" | "steel" | "hardware" | "chemical" | "formwork">;
  paymentTerms: string;
  leadTimeDays: number;
  notes: string;
  status: "active" | "inactive";
  createdAt: string;
  updatedAt: string;
}

export const mockStructureCastVendors: StructureCastVendor[] = [
  {
    id: "sc-vendor-1",
    vendorCode: "CALPORT",
    vendorName: "CalPortland — Santa Maria",
    contactName: "Inside Sales — Santa Maria Plant",
    contactEmail: "santamaria-sales@calportland.com",
    contactPhone: "805-925-6791",
    address: "2474 Skyway Dr",
    city: "Santa Maria",
    state: "CA",
    zipCode: "93455",
    categories: ["concrete"],
    paymentTerms: "Net 30",
    leadTimeDays: 1,
    notes: "Primary concrete supplier. 5,000 PSI Type II/V mix. Same-day delivery available with 24-hr notice. Trial batches on call.",
    status: "active",
    createdAt: "2025-05-29T00:00:00.000Z",
    updatedAt: "2026-01-15T00:00:00.000Z",
  },
  {
    id: "sc-vendor-2",
    vendorCode: "COASTREB",
    vendorName: "Coast Steel & Rebar",
    contactName: "Shawn Mendoza",
    contactEmail: "smendoza@coaststeelrebar.com",
    contactPhone: "805-922-4471",
    address: "1850 W Betteravia Rd",
    city: "Santa Maria",
    state: "CA",
    zipCode: "93455",
    categories: ["steel"],
    paymentTerms: "Net 30",
    leadTimeDays: 5,
    notes: "#4 and #6 ASTM A-706/A-615 Gr 60. Will cut to 20-ft lengths on request. Discount on bundles over 2000 lf.",
    status: "active",
    createdAt: "2025-05-29T00:00:00.000Z",
    updatedAt: "2026-01-20T00:00:00.000Z",
  },
  {
    id: "sc-vendor-3",
    vendorCode: "PRESEAL",
    vendorName: "Press-Seal Corporation",
    contactName: "West Coast Sales",
    contactEmail: "westcoast@press-seal.com",
    contactPhone: "260-436-0521",
    address: "2424 W State Blvd",
    city: "Fort Wayne",
    state: "IN",
    zipCode: "46808",
    categories: ["hardware"],
    paymentTerms: "Net 30",
    leadTimeDays: 10,
    notes: "HWB cast-in pipe boots (8\", 10\", 12\"). FOB Fort Wayne IN. Freight typical 7-10 days to CA.",
    status: "active",
    createdAt: "2025-05-29T00:00:00.000Z",
    updatedAt: "2026-01-10T00:00:00.000Z",
  },
  {
    id: "sc-vendor-4",
    vendorCode: "ALHFDRY",
    vendorName: "Alhambra Foundry Co.",
    contactName: "CA Sales Office",
    contactEmail: "sales@alhambrafoundry.com",
    contactPhone: "209-466-7861",
    address: "1100 E Worth St",
    city: "Stockton",
    state: "CA",
    zipCode: "95205",
    categories: ["hardware"],
    paymentTerms: "Net 30",
    leadTimeDays: 14,
    notes: "1900 Sanitary Sewer CPH frames + covers. ASTM A-48 CL 35B gray iron. H20 wheel loading rating.",
    status: "active",
    createdAt: "2025-05-29T00:00:00.000Z",
    updatedAt: "2025-12-20T00:00:00.000Z",
  },
  {
    id: "sc-vendor-5",
    vendorCode: "CSI",
    vendorName: "Concrete Sealants Inc",
    contactName: "Customer Service",
    contactEmail: "service@conseal.com",
    contactPhone: "937-845-8776",
    address: "1421 Greenwood Lake Rd",
    city: "New Carlisle",
    state: "OH",
    zipCode: "45344",
    categories: ["chemical"],
    paymentTerms: "Net 30",
    leadTimeDays: 7,
    notes: "CS-102 butyl sealant rolls (1-1/8\"x14.5'). Pallet pricing for 24+ boxes. Currently on back-order for 3 boxes.",
    status: "active",
    createdAt: "2025-05-29T00:00:00.000Z",
    updatedAt: "2026-01-12T00:00:00.000Z",
  },
  {
    id: "sc-vendor-6",
    vendorCode: "NOXCRETE",
    vendorName: "Nox-Crete Products Group",
    contactName: "West Coast Rep",
    contactEmail: "westrep@nox-crete.com",
    contactPhone: "402-341-2080",
    address: "1444 S 20th St",
    city: "Omaha",
    state: "NE",
    zipCode: "68108",
    categories: ["chemical"],
    paymentTerms: "Net 30",
    leadTimeDays: 3,
    notes: "Form release oil (standard precast grade). 55-gal drums or 5-gal pails.",
    status: "active",
    createdAt: "2025-05-29T00:00:00.000Z",
    updatedAt: "2026-01-18T00:00:00.000Z",
  },
  {
    id: "sc-vendor-7",
    vendorCode: "FORTA",
    vendorName: "Forta Concrete Fiber",
    contactName: "Inside Sales",
    contactEmail: "sales@forta-ferro.com",
    contactPhone: "800-245-0306",
    address: "100 Forta Dr",
    city: "Grove City",
    state: "PA",
    zipCode: "16127",
    categories: ["chemical"],
    paymentTerms: "Net 30",
    leadTimeDays: 7,
    notes: "Polypropylene fibers — secondary reinforcing per Caltrans spec. Comes in 1-lb dosage bags.",
    status: "active",
    createdAt: "2025-05-29T00:00:00.000Z",
    updatedAt: "2026-01-15T00:00:00.000Z",
  },
];

export const createMockStructureCastVendorStore = () =>
  new MockServiceStore<StructureCastVendor>({
    mockData: mockStructureCastVendors,
    idField: "id",
    simulateDelay: 200,
    persistKey: "preferred-structurecast-vendors",
  });

export default mockStructureCastVendors;
